/**
 * Connector OAuth Popup Utility
 *
 * Handles OAuth flow in a popup window with message passing back to parent.
 *
 * Cross-origin OAuth redirects (e.g., Shopify) can cause popup.closed to
 * flicker true during navigation, so we use 4 channels with a grace period:
 * 1. storage event (fires instantly when popup writes to localStorage)
 * 2. postMessage (works when window.opener preserved — same-origin only)
 * 3. BroadcastChannel (reliable same-origin cross-tab communication)
 * 4. Poll timer with grace period (fallback for localStorage after popup closes)
 */

export interface OAuthResult {
  success: boolean;
  connectionId?: string;
  connectorId?: string;
  connectorName?: string;
  error?: string;
  reauthorized?: boolean;
}

const POPUP_WIDTH = 600;
const POPUP_HEIGHT = 700;
const OAUTH_POPUP_NAME = 'connector-oauth';
const OAUTH_RESULT_STORAGE_KEY = 'connector-oauth-result';

/**
 * Open OAuth flow in a popup window
 * Returns a promise that resolves when the popup completes (success or error)
 */
export function openOAuthPopup(authUrl: string): Promise<OAuthResult> {
  return new Promise((resolve) => {
    let resolved = false;
    const resolveOnce = (result: OAuthResult): void => {
      if (resolved) return;
      resolved = true;
      cleanup();
      resolve(result);
    };

    const left = window.screen.width / 2 - POPUP_WIDTH / 2;
    const top = window.screen.height / 2 - POPUP_HEIGHT / 2;

    const popup = window.open(
      authUrl,
      OAUTH_POPUP_NAME,
      `width=${POPUP_WIDTH},height=${POPUP_HEIGHT},left=${left},top=${top},toolbar=no,location=yes,status=no,menubar=no,scrollbars=yes`,
    );

    if (!popup) {
      resolve({
        success: false,
        error: 'Popup blocked. Please allow popups for this site.',
      });
      return;
    }

    // Shared cleanup function
    let pollTimer: ReturnType<typeof setInterval>;
    let graceTimer: ReturnType<typeof setTimeout>;
    let timeoutTimer: ReturnType<typeof setTimeout>;
    let bc: BroadcastChannel | null = null;

    const cleanup = (): void => {
      clearInterval(pollTimer);
      clearTimeout(graceTimer);
      clearTimeout(timeoutTimer);
      window.removeEventListener('message', handleMessage);
      window.removeEventListener('storage', handleStorage);
      bc?.close();
      if (popup && !popup.closed) {
        popup.close();
      }
    };

    // Helper: try reading result from localStorage
    const tryReadLocalStorage = (): OAuthResult | null => {
      try {
        const stored = localStorage.getItem(OAUTH_RESULT_STORAGE_KEY);
        if (stored) {
          localStorage.removeItem(OAUTH_RESULT_STORAGE_KEY);
          return JSON.parse(stored) as OAuthResult;
        }
      } catch {
        // ignore parse errors
      }
      return null;
    };

    // Channel 1: storage event (fires instantly when popup writes to localStorage)
    const handleStorage = (event: StorageEvent): void => {
      if (event.key === OAUTH_RESULT_STORAGE_KEY && event.newValue) {
        try {
          const result = JSON.parse(event.newValue) as OAuthResult;
          localStorage.removeItem(OAUTH_RESULT_STORAGE_KEY);
          resolveOnce(result);
        } catch {
          // ignore parse errors
        }
      }
    };
    window.addEventListener('storage', handleStorage);

    // Channel 2: postMessage (works when window.opener preserved)
    const handleMessage = (event: MessageEvent): void => {
      if (event.origin !== window.location.origin) return;
      if (event.data?.type === 'CONNECTOR_OAUTH_RESULT') {
        resolveOnce(event.data.payload as OAuthResult);
      }
    };
    window.addEventListener('message', handleMessage);

    // Channel 3: BroadcastChannel
    try {
      bc = new BroadcastChannel(OAUTH_POPUP_NAME);
      bc.onmessage = (event) => {
        if (event.data?.type === 'CONNECTOR_OAUTH_RESULT') {
          resolveOnce(event.data.payload as OAuthResult);
        }
      };
    } catch {
      // BroadcastChannel not supported in some browsers
    }

    // Clear any stale result from previous OAuth attempts
    localStorage.removeItem(OAUTH_RESULT_STORAGE_KEY);

    // Channel 4: Poll for popup close with grace period.
    // Cross-origin redirects (e.g., Shopify) can cause popup.closed to
    // briefly flicker true during navigation. The grace period ensures
    // we don't give up before the callback page loads and sends results.
    const GRACE_PERIOD_MS = 3000;
    const GRACE_CHECK_INTERVAL_MS = 300;

    pollTimer = setInterval(() => {
      if (popup.closed) {
        clearInterval(pollTimer);

        // Check localStorage immediately
        const immediate = tryReadLocalStorage();
        if (immediate) {
          resolveOnce(immediate);
          return;
        }

        // Grace period: keep checking localStorage and keep other listeners active
        let elapsed = 0;
        graceTimer = setInterval(() => {
          elapsed += GRACE_CHECK_INTERVAL_MS;
          const result = tryReadLocalStorage();
          if (result) {
            clearInterval(graceTimer);
            resolveOnce(result);
            return;
          }
          if (elapsed >= GRACE_PERIOD_MS) {
            clearInterval(graceTimer);
            resolveOnce({
              success: false,
              error: 'Authentication cancelled',
            });
          }
        }, GRACE_CHECK_INTERVAL_MS) as unknown as ReturnType<typeof setTimeout>;
      }
    }, 500);

    // Timeout after 5 minutes
    timeoutTimer = setTimeout(() => {
      resolveOnce({
        success: false,
        error: 'Authentication timed out',
      });
    }, 5 * 60 * 1000);
  });
}

/**
 * Check if current window is a popup.
 * Uses multiple signals since cross-origin redirects (e.g., Shopify OAuth)
 * can break window.opener and window.name references.
 */
export function isPopupWindow(): boolean {
  const hasOpener = window.opener !== null && window.opener !== window;
  const hasPopupName = window.name === OAUTH_POPUP_NAME;
  return hasOpener || hasPopupName;
}

/**
 * Send OAuth result to parent window and close popup.
 * Uses all available channels to maximize delivery reliability,
 * since cross-origin redirects (e.g., Shopify) break window.opener.
 */
export function sendOAuthResultToParent(result: OAuthResult): void {
  // Method 1: localStorage (parent listens via storage event + polls on close)
  try {
    localStorage.setItem(OAUTH_RESULT_STORAGE_KEY, JSON.stringify(result));
  } catch {
    // localStorage may be unavailable in some contexts
  }

  // Method 2: postMessage (works when window.opener preserved)
  if (window.opener) {
    window.opener.postMessage(
      {
        type: 'CONNECTOR_OAUTH_RESULT',
        payload: result,
      },
      window.location.origin,
    );
  }

  // Method 3: BroadcastChannel (reliable same-origin cross-tab communication)
  try {
    const bc = new BroadcastChannel(OAUTH_POPUP_NAME);
    bc.postMessage({ type: 'CONNECTOR_OAUTH_RESULT', payload: result });
    // Don't call bc.close() — let it be GC'd when popup closes.
  } catch {
    // BroadcastChannel not supported in some browsers
  }

  // Close popup after delay to ensure messages are delivered
  setTimeout(() => window.close(), 300);
}
